import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, FileText, Car, User, Download, Loader2 } from 'lucide-react';
import { BulkAstsPublicResponse, BulkAstsPublicResultItem, generateBulkAstsPublicPdf, localizeRateLimitMessage } from '../services/astsPublicService';
import { makeAuthenticatedRequest } from '../services/apiService';
import { API_ENDPOINTS } from '../config/api';
import { formatMvrDate } from '../services/mvrService';
import AstsPublicLienholders from './AstsPublicLienholders';

interface BulkAstsPublicResultsProps {
  results: BulkAstsPublicResponse;
}

const DataField: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <div className="text-xs text-[#64748b] mb-0.5">{label}</div>
      <div className="font-medium text-[#0f172a]">{value}</div>
    </div>
  );
};

const downloadItemPdf = async (stateCode: string, vin: string): Promise<boolean> => {
  try {
    const url = `${API_ENDPOINTS.ASTS_PUBLIC.PDF(stateCode)}?vin=${encodeURIComponent(vin)}`;
    const response = await makeAuthenticatedRequest(url);
    if (!response.ok) return false;
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `ASTS_Public_${stateCode}_${vin}_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(objectUrl);
    return true;
  } catch {
    return false;
  }
};

const BulkAstsPublicResults: React.FC<BulkAstsPublicResultsProps> = ({ results }) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [pdfLoadingItems, setPdfLoadingItems] = useState<Set<number>>(new Set());
  const [allPdfLoading, setAllPdfLoading] = useState(false);

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const handleItemPdf = async (item: BulkAstsPublicResultItem) => {
    setPdfLoadingItems(prev => new Set(prev).add(item.index));
    await downloadItemPdf(item.stateCode, item.vin);
    setPdfLoadingItems(prev => { const next = new Set(prev); next.delete(item.index); return next; });
  };

  const handleAllPdfs = async () => {
    const successItems = results.results.filter(r => r.success && (r.titleResponse?.titles?.length ?? 0) > 0);
    if (successItems.length === 0) return;
    setAllPdfLoading(true);
    try {
      await generateBulkAstsPublicPdf(results.results);
    } catch (err) {
      console.error('Failed to generate combined ASTS Public PDF', err);
    } finally {
      setAllPdfLoading(false);
    }
  };

  const successWithTitles = results.results.filter(r => r.success && (r.titleResponse?.titles?.length ?? 0) > 0);

  return (
    <div className="space-y-4">
      {/* Summary Banner */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-black/[0.08] p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-[#64748b]">Total:</span>
            <span className="font-bold text-[#0f172a]">{results.totalRequested}</span>
          </div>
          <div className="flex items-center gap-2 text-[#16a34a]">
            <CheckCircle2 className="w-4 h-4" />
            <span>{results.totalSucceeded} succeeded</span>
          </div>
          {results.totalFailed > 0 && (
            <div className="flex items-center gap-2 text-[#dc2626]">
              <XCircle className="w-4 h-4" />
              <span>{results.totalFailed} failed</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-3">
            {successWithTitles.length > 0 && (
              <button
                onClick={handleAllPdfs}
                disabled={allPdfLoading}
                className="flex items-center gap-2 px-4 py-1.5 bg-[#c41e3a] text-white rounded-lg text-xs font-medium hover:bg-[#a01830] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {allPdfLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
                ) : (
                  <><Download className="w-3.5 h-3.5" /> Generate All PDFs ({successWithTitles.length})</>
                )}
              </button>
            )}
            <span className="text-xs text-[#94a3b8]">
              Free public inquiry — no charges applied
            </span>
          </div>
        </div>
      </div>

      {/* Results List */}
      <div className="space-y-3">
        {results.results.map((item, index) => (
          <ResultItem
            key={index}
            item={item}
            isExpanded={expandedItems.has(index)}
            onToggle={() => toggleExpanded(index)}
            pdfLoading={pdfLoadingItems.has(index)}
            onGeneratePdf={() => handleItemPdf(item)}
          />
        ))}
      </div>
    </div>
  );
};

interface ResultItemProps {
  item: BulkAstsPublicResultItem;
  isExpanded: boolean;
  onToggle: () => void;
  pdfLoading: boolean;
  onGeneratePdf: () => void;
}

const ResultItem: React.FC<ResultItemProps> = ({ item, isExpanded, onToggle, pdfLoading, onGeneratePdf }) => {
  const titles = item.titleResponse?.titles ?? [];

  let vehicleDescription = '';
  if (titles.length > 0) {
    const t = titles[0];
    vehicleDescription = [t.year, t.make, t.model].filter(Boolean).join(' ');
  }

  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-xl border overflow-hidden transition-all shadow-sm ${
      item.success ? 'border-black/[0.08]' : 'border-[#dc2626]/30'
    }`}>
      {/* Header Row */}
      <button
        onClick={onToggle}
        disabled={!item.success}
        className={`w-full px-4 py-3 flex items-center justify-between text-left transition-all ${
          item.success ? 'hover:bg-black/[0.02] cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <span className="text-[#94a3b8] text-sm font-medium w-6 text-center flex-shrink-0">
            {item.index + 1}.
          </span>
          <span className="font-mono text-sm text-[#0f172a] font-medium">{item.vin}</span>
          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded flex-shrink-0">
            {item.stateCode}
          </span>
          {vehicleDescription && (
            <span className="text-sm text-[#64748b] truncate hidden sm:block">{vehicleDescription}</span>
          )}
          {item.referenceNote && (
            <span className="text-xs text-[#94a3b8] truncate hidden md:block">Ref: {item.referenceNote}</span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {item.success ? (
            <div className="flex items-center gap-2">
              {titles.length > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); onGeneratePdf(); }}
                  disabled={pdfLoading}
                  className="flex items-center gap-1 px-2 py-1 text-[#c41e3a] hover:bg-[#c41e3a]/10 rounded text-xs font-medium transition-colors disabled:opacity-50"
                  title="Generate PDF"
                >
                  {pdfLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  PDF
                </button>
              )}
              <span className="px-2 py-1 bg-[#16a34a]/10 text-[#16a34a] text-xs font-medium rounded">Success</span>
              {isExpanded ? <ChevronUp className="w-4 h-4 text-[#94a3b8]" /> : <ChevronDown className="w-4 h-4 text-[#94a3b8]" />}
            </div>
          ) : (
            <span className="px-2 py-1 bg-[#dc2626]/10 text-[#dc2626] text-xs font-medium rounded flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Failed
            </span>
          )}
        </div>
      </button>

      {/* Error Message */}
      {!item.success && item.errorMessage && (
        <div className="px-4 pb-3 text-sm text-[#dc2626]">{localizeRateLimitMessage(item.errorMessage, item.availableAgainAtUtc)}</div>
      )}

      {/* Expanded Content */}
      {item.success && isExpanded && (
        <div className="border-t border-black/[0.06] p-4 space-y-4">
          {/* Title Records */}
          {titles.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#64748b]" />
                <span className="text-sm font-semibold text-[#374151]">Title Records ({titles.length})</span>
              </div>
              {titles.map((title, i) => (
                <div key={i} className="bg-[#f8fafc] rounded-lg p-4 space-y-4">
                  {/* Vehicle */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Car className="w-4 h-4 text-[#64748b]" />
                      <span className="text-sm font-semibold text-[#374151]">Vehicle</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <DataField label="VIN" value={title.vin} />
                      <DataField label="Year" value={title.year} />
                      <DataField label="Make" value={title.make} />
                      <DataField label="Model" value={title.model} />
                      <DataField label="Body Style" value={title.bodyStyle} />
                      <DataField label="Color" value={title.color} />
                      <DataField label="Odometer" value={title.odometerReading != null ? `${title.odometerReading.toLocaleString()} mi` : null} />
                      <DataField label="Odometer Status" value={title.odometerStatus} />
                    </div>
                  </div>
                  {/* Title Info */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-[#64748b]" />
                      <span className="text-sm font-semibold text-[#374151]">Title</span>
                      {title.titleFormat && (
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${
                          title.titleFormat.toLowerCase() === 'paper'
                            ? 'bg-orange-100 text-orange-700'
                            : title.titleFormat.toLowerCase() === 'electronic'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-[#f1f5f9] text-[#64748b]'
                        }`}>{title.titleFormat}</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <DataField label="Title Number" value={title.titleNumber} />
                      <DataField label="Control Number" value={title.controlNumber} />
                      <DataField label="Title Status" value={title.titleStatus} />
                      <DataField label="Title Date" value={formatMvrDate(title.titleDate)} />
                      <DataField label="Title Type" value={title.titleType} />
                      <DataField label="Purchase Price" value={title.purchasePrice != null ? `$${title.purchasePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null} />
                      {title.ownerCounty && <DataField label="County of Residence" value={title.ownerCounty} />}
                      {title.brand && <DataField label="Brand" value={title.brand} />}
                    </div>
                  </div>
                  {/* Owner */}
                  {title.ownerName && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <User className="w-4 h-4 text-[#64748b]" />
                        <span className="text-sm font-semibold text-[#374151]">Owner</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="col-span-2"><DataField label="Name" value={title.ownerName} /></div>
                        <DataField label="City" value={title.ownerCity} />
                        <DataField label="State" value={title.ownerState} />
                        <div className="col-span-2"><DataField label="Address" value={title.ownerAddress} /></div>
                        <DataField label="ZIP" value={title.ownerZip} />
                      </div>
                    </div>
                  )}
                  {/* Lienholders */}
                  <AstsPublicLienholders title={title} />
                </div>
              ))}
            </div>
          )}

          {/* Registration Records — hidden, reserved for future use */}

          {/* No data */}
          {titles.length === 0 && (
            <div className="text-sm text-[#64748b] text-center py-4">
              Search completed but no records found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkAstsPublicResults;
